import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import TeacherLoginPage from "./pages/TeacherLoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFoundPage from "./pages/NotFoundPage";
import BadRequestPage from "./pages/BadRequestPage";
import ErrorPage from "./pages/ErrorPage";
import LogoutPage from "./pages/LogoutPage";
import Sidebar from "./components/Sidebar";
import { HelmetProvider } from "react-helmet-async";
import PageTitle from "./components/PageTitle";
import { jwtDecode } from "jwt-decode";
import { ToastContainer } from "react-toastify";
import { auth } from "./firebase-config";
import { signOut, onIdTokenChanged, signInWithCustomToken } from "firebase/auth";
import { getRequest } from "./apiWrapper"; // API呼び出し用ラッパー関数
import getBrandFromHostname from "./utils/getBrandFromHostname";
import { HOSTS } from "./constants/hosts";
import AppReportRouter from "./AppReportRouter";
import AppScheduleRouter from "./AppScheduleRouter";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(""); // "teacher" or "admin"
  const [schoolName, setSchoolName] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]); // 今日の生徒で選択された生徒
  const [isLoading, setIsLoading] = useState(true);
  // 画面幅が一定以上かどうかを管理する state（ここでは1024pxを閾値とする例）
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [authCheckFinished, setAuthCheckFinished] = useState(false);

  // ドメイン別ルーティング用
  // ブランド名を動的に取得
  const brand = getBrandFromHostname();
  const location = useLocation();
  const getHostname = () => new URL(window.location.href).hostname;
  const isAdminLogin = () => new URL(window.location.href).hostname === `${HOSTS.adminLogin}.${getBrandFromHostname()}.classora.jp`;
  const isTeacherLogin = () => new URL(window.location.href).hostname === `${HOSTS.teacherLogin}.${getBrandFromHostname()}.classora.jp`;
  const loginHosts = [
    HOSTS.login,
    HOSTS.adminLogin,
    HOSTS.teacherLogin,
  ];

  // reportアプリ用ドメインかどうか
  const isReportApp =
    getHostname().startsWith(`${HOSTS.adminReport}.${brand}`) ||
    getHostname().startsWith(`${HOSTS.teacherReport}.${brand}`);

  const isScheduleApp =
    getHostname().startsWith(`${HOSTS.adminSchedule}.${brand}`) ||
    getHostname().startsWith(`${HOSTS.teacherSchedule}.${brand}`);

  // appName を判定
  const appName = isScheduleApp ? "schedule" : "report";


  onIdTokenChanged(auth, (user) => {
    if (user) {
      user.getIdToken().then((token) => {
        sessionStorage.setItem("jwtToken", token);
        // console.log("Updated token via onIdTokenChanged");
      });
    } else {
      sessionStorage.setItem("jwtToken", null);
    }
  });

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // 🔹 ログアウト処理（Firebase のセッションも削除）
  const handleLogout = useCallback(async () => {
    console.log("start logout");
    try {
      sessionStorage.clear();
      setIsLoggedIn(false);
      setUserRole("");
      setTeacherName("");
      setAdminEmail("");
      setSelectedStudents([]);
      await signOut(auth);
    } catch (error) {
      console.error("❌ ログアウトエラー:", error);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, []);

  const initAuth = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    let idToken = null;
    if (token) {
      console.log("get token from param...");
      try {
        const userCredential = await signInWithCustomToken(auth, token);
        const user = userCredential.user;

        // ここでトークンを取得
        idToken = await user.getIdToken();

        const decoded = jwtDecode(idToken);
        const role = decoded.role;
        const email = decoded.email || "";

        sessionStorage.setItem("jwtToken", idToken);
        sessionStorage.setItem("userRole", role);
        if (role === "admin") {
          sessionStorage.setItem("adminEmail", email);
        } else if (role === "teacher") {
        }

        // URLからtoken削除
        window.history.replaceState(null, "", window.location.pathname);
      } catch (error) {
        console.error("JWTデコード失敗:", error);
      }
    }

    const storedToken = idToken || sessionStorage.getItem("jwtToken");
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        const currentTime = Date.now() / 1000;
        if (decoded.exp > currentTime) {
          console.log("decoded jwt token.");
          setIsLoggedIn(true);

          const storedUserRole = sessionStorage.getItem("userRole") || "";
          setUserRole(storedUserRole);

          if (storedUserRole === "teacher") {
            const storedTeacherName = sessionStorage.getItem("teacherName");
            if (!storedTeacherName) {
              getRequest("teacherApprovalStatus").then((teacherData) => {
                if (teacherData?.name && teacherData?.id) {
                  sessionStorage.setItem("teacherName", teacherData.name);
                  sessionStorage.setItem("teacherid", teacherData.id);
                  setTeacherName(teacherData.name);
                  console.log("teacher name:", teacherData.name);
                }
              }).catch((e) => {
                console.error("teacher情報取得失敗:", e);
              });
            } else {
              setTeacherName(storedTeacherName);
            }
            const selStudents = sessionStorage.getItem("selectedStudents");
            if (!selStudents) {
              fetchSelectedStudents();
            } else {
              setSelectedStudents(JSON.parse(selStudents));
            }
          }
          if (storedUserRole === "admin") {
            setAdminEmail(sessionStorage.getItem("adminEmail") || "");
            getRequest("schools").then((schoolData) => {
              if (schoolData?.school?.name) {
                sessionStorage.setItem("schoolName", schoolData.school.name);
                setSchoolName(schoolData.school.name);
              }
            }).catch((e) => {
              console.error("schoolName取得失敗:", e);
            });
          }
        } else {
          console.warn("Token expired, logging out...");
          handleLogout();
        }
      } catch (error) {
        console.error("Invalid token:", error);
        handleLogout();
      }
    } else {
      console.warn("Can't get token from sessionStorage");
    }

    console.log("init isLoggedIn =", isLoggedIn);

    setIsLoading(false);
    setAuthCheckFinished(true);
  };

  // ロール別情報をAPIから取得
  useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    if (role === "admin") {
      getRequest("schools").then((schoolData) => {
        if (schoolData?.school?.name) {
          sessionStorage.setItem("schoolName", schoolData.school.name);
        }
      }).catch((e) => {
        console.error("schoolName取得失敗:", e);
      });
    }

  }, [userRole]);

  const handleLogin = (role, data) => {
  };

  // 🔹 API から選択した生徒を取得
  const fetchSelectedStudents = async () => {
    try {
      const data = await getRequest("teacherStudents");
      if (data && data.selectedStudents) {
        setSelectedStudents(data.selectedStudents);
        console.log("選択された生徒:", data.selectedStudents);
      } else {
        setSelectedStudents([]);
        sessionStorage.removeItem("selectedStudents");
      }
    } catch (error) {
      console.error("API のデータ取得エラー:", error);
    }
  };

  const getLoginUrl = () => {
    const hostname = getHostname();
    if (hostname.startsWith(`${HOSTS.adminReport}.${brand}`)) {
      return `https://${HOSTS.adminLogin}.${brand}.classora.jp`;
    } else if (hostname.startsWith(`${HOSTS.teacherReport}.${brand}`)) {
      return `https://${HOSTS.teacherLogin}.${brand}.classora.jp`;
    } else if (hostname.startsWith(`${HOSTS.adminSchedule}.${brand}`)) {
      return `https://${HOSTS.adminLogin}.${brand}.classora.jp?redirectTo=schedule`;
    } else if (hostname.startsWith(`${HOSTS.teacherSchedule}.${brand}`)) {
      return `https://${HOSTS.teacherLogin}.${brand}.classora.jp?redirectTo=schedule`;
    } else {
      return `https://${HOSTS.login}.${brand}.classora.jp`;
    }
  };

  // サービスログインドメインのルーティング
  if (getHostname() === "login.classora.jp") {
    return (
      <HelmetProvider>
        <PageTitle appName={appName} />
        <Routes>
          <Route path="/welcome" element={<ResetPasswordPage />} />
          {/* もし /welcome 以外のパスでアクセスされたら /welcome にリダイレクト */}
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </HelmetProvider>
    );
  }

  // Reportアプリなら専用ルーターを返す
  if (isReportApp) {
    return (
      <div className="flex">
        {authCheckFinished && !isLoading && isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && (
          <Sidebar
            userRole={userRole}
            onLogout={handleLogout}
            teacherName={teacherName}
            schoolName={schoolName}
            appName={appName}
          />
        )}
        <div className={`flex-1 w-full ${isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && isDesktop ? "ml-64" : ""} p-4 transition-all`}>
          {authCheckFinished && !isLoading && (
            <HelmetProvider>
              <PageTitle appName={appName} />
              <AppReportRouter
                isLoggedIn={isLoggedIn}
                userRole={userRole}
                teacherName={teacherName}
                adminEmail={adminEmail}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                isAdminLogin={isAdminLogin}
                isTeacherLogin={isTeacherLogin}
                getLoginUrl={getLoginUrl}
                handleLogout={handleLogout}
              />

            </HelmetProvider>
          )}
        </div>
        <ToastContainer />
      </div>
    );
  }
  if (isScheduleApp) {
    return (
      <div className="flex">
        {authCheckFinished && !isLoading && isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && (
          <Sidebar
            userRole={userRole}
            onLogout={handleLogout}
            teacherName={teacherName}
            schoolName={schoolName}
            appName={appName}
          />
        )}
        <div className={`flex-1 w-full ${isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && isDesktop ? "ml-64" : ""} p-4 transition-all`}>
          {authCheckFinished && !isLoading && (
            <HelmetProvider>
              <PageTitle appName={appName} />
              <AppScheduleRouter
                isLoggedIn={isLoggedIn}
                userRole={userRole}
                teacherName={teacherName}
                adminEmail={adminEmail}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                isAdminLogin={isAdminLogin}
                isTeacherLogin={isTeacherLogin}
                getLoginUrl={getLoginUrl}
                handleLogout={handleLogout}
              />

            </HelmetProvider>
          )}
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="flex">
      {authCheckFinished && !isLoading && isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && (
        <Sidebar
          userRole={userRole}
          onLogout={handleLogout}
          teacherName={teacherName}
          schoolName={schoolName}
          appName={appName}
        />
      )}
      <div className={`flex-1 w-full ${isLoggedIn && !loginHosts.some(prefix => getHostname().startsWith(prefix)) && isDesktop ? "ml-64" : ""} p-4 transition-all`}>
        {authCheckFinished && !isLoading && (
          <HelmetProvider>
            <PageTitle appName={appName} />
            <Routes>
              <Route
                path="/"
                element={
                  isAdminLogin() ? (
                    <AdminLoginPage onLogin={(email) => handleLogin("admin", email)} />
                  ) : isTeacherLogin() ? (
                    <TeacherLoginPage onLogin={(id, name) => handleLogin("teacher", name)} />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              <Route path="/error/400" element={<BadRequestPage />} />
              <Route path="/error/404" element={<NotFoundPage />} />
              <Route path="/error/500" element={<ErrorPage />} />
              <Route path="/logout" element={<LogoutPage onLogout={handleLogout} />} />
              <Route path="*" element={<Navigate to="/error/404" replace />} />
            </Routes>
          </HelmetProvider>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default App;
